import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Phone,
  MapPin,
  Star,
  Clock,
  Shield,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  ArrowLeft,
  Home,
  Building2,
  Sofa,
  Package,
  HardHat,
  Trash2,
  Facebook,
  ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { SERVICES_DATA, BUSINESS_INFO, generateServiceSchema } from "../data/servicesData";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trash-haulers-tx/artifacts/52ejxn9j_dallas%20haul%20stars.jpg";
const PHONE_NUMBER = "972-330-3678";

// Icon mapping
const iconMap = {
  Home: Home,
  Building2: Building2,
  Sofa: Sofa,
  Package: Package,
  HardHat: HardHat,
  Trash2: Trash2
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// FAQ Accordion Component
const FAQAccordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = React.useState(0);

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
          className="border border-slate-200 rounded-xl overflow-hidden bg-white"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
            data-testid={`faq-${index}`}
            aria-expanded={openIndex === index}
          >
            <span className="font-dm font-semibold text-brand-navy pr-4">{faq.question}</span>
            <ChevronDown
              className={`w-5 h-5 text-brand-primary flex-shrink-0 transition-transform duration-200 ${
                openIndex === index ? "rotate-180" : ""
              }`}
            />
          </button>
          {openIndex === index && (
            <div className="px-6 pb-6">
              <p className="text-slate-600 font-dm faq-answer">{faq.answer}</p>
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );
};

// Service Page Component
const ServicePage = () => {
  const { slug } = useParams();
  const service = SERVICES_DATA[slug];

  // If service not found
  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="font-barlow font-bold text-4xl text-brand-navy mb-4">Service Not Found</h1>
          <p className="text-slate-600 font-dm mb-6">The service you're looking for doesn't exist.</p>
          <Button asChild>
            <Link to="/">Return Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[service.icon] || Home;
  const schema = generateServiceSchema(service, BUSINESS_INFO);

  return (
    <>
      {/* SEO Head */}
      <Helmet>
        <title>{service.title}</title>
        <meta name="description" content={service.metaDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${BUSINESS_INFO.url}/services/${service.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={service.title} />
        <meta property="og:description" content={service.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${BUSINESS_INFO.url}/services/${service.slug}`} />
        <meta property="og:image" content={service.image} />
        <meta property="og:site_name" content={BUSINESS_INFO.name} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={service.title} />
        <meta name="twitter:description" content={service.metaDescription} />
        <meta name="twitter:image" content={service.image} />
        
        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-20">
              <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
                <img src={LOGO_URL} alt="Dallas Haul Stars Logo" className="h-14 w-auto rounded-lg" />
              </Link>
              <div className="hidden lg:flex items-center gap-6">
                <Link to="/#services" className="text-slate-700 hover:text-brand-primary font-dm font-medium">Services</Link>
                <Link to="/#why-us" className="text-slate-700 hover:text-brand-primary font-dm font-medium">Why Us</Link>
                <Link to="/#reviews" className="text-slate-700 hover:text-brand-primary font-dm font-medium">Reviews</Link>
                <Link to="/#areas" className="text-slate-700 hover:text-brand-primary font-dm font-medium">Areas</Link>
              </div>
              <div className="flex items-center gap-4">
                <a href={`tel:${PHONE_NUMBER}`} className="hidden md:flex items-center gap-2 text-brand-primary font-dm font-bold text-lg" data-testid="nav-phone">
                  <Phone className="w-5 h-5" />
                  {PHONE_NUMBER}
                </a>
                <Button asChild className="bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold px-6 rounded-md" data-testid="nav-cta">
                  <Link to="/#contact">Get Free Quote</Link>
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="pt-20 bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm font-dm" aria-label="Breadcrumb">
              <Link to="/" className="text-slate-500 hover:text-brand-primary">Home</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <Link to="/#services" className="text-slate-500 hover:text-brand-primary">Services</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="text-brand-navy font-medium">{service.h1.replace(" in Dallas, TX", "")}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-brand-navy via-slate-900 to-brand-navy py-16 lg:py-24">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 bg-brand-primary/20 rounded-xl flex items-center justify-center">
                    <IconComponent className="w-7 h-7 text-brand-primary" />
                  </div>
                  <span className="bg-brand-primary/20 text-brand-primary font-dm font-semibold px-4 py-2 rounded-full text-sm border border-brand-primary/30">
                    {service.pricing}
                  </span>
                </div>

                <h1 className="font-barlow font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight mb-4" data-testid="service-h1">
                  {service.h1}
                </h1>

                <p className="text-lg sm:text-xl text-slate-300 font-dm mb-8 hero-subtitle">
                  {service.heroSubtitle}
                </p>

                <div className="flex flex-wrap gap-3 mb-8">
                  {service.features.slice(0, 4).map((feature, index) => (
                    <span key={index} className="flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-dm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      {feature}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold text-lg px-8 py-6 rounded-md transition-all hover:scale-105"
                    data-testid="hero-cta"
                  >
                    <Link to="/#contact" className="flex items-center gap-2">
                      {service.ctaText}
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="border-2 border-white text-white hover:bg-white hover:text-brand-navy font-dm font-bold text-lg px-8 py-6 rounded-md"
                    data-testid="hero-phone"
                  >
                    <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2">
                      <Phone className="w-5 h-5" />
                      Call {PHONE_NUMBER}
                    </a>
                  </Button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative hidden lg:block"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.imageAlt}
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Main Content Sections */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {service.sections.map((section, sectionIndex) => (
              <motion.article
                key={sectionIndex}
                {...fadeInUp}
                className="mb-16 last:mb-0"
              >
                <h2 className="font-barlow font-bold text-2xl sm:text-3xl text-brand-navy mb-6" data-testid={`section-h2-${sectionIndex}`}>
                  {section.h2}
                </h2>
                
                <p className="text-slate-700 font-dm text-lg leading-relaxed mb-6">
                  {section.content}
                </p>

                {section.list && (
                  <ul className="space-y-3 mb-6">
                    {section.list.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-3 text-slate-700 font-dm">
                        <CheckCircle className="w-5 h-5 text-brand-primary mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.steps && (
                  <div className="grid md:grid-cols-3 gap-6 mt-8">
                    {section.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                        <div className="w-10 h-10 bg-brand-primary rounded-lg flex items-center justify-center text-white font-barlow font-bold mb-4">
                          {stepIndex + 1}
                        </div>
                        <h3 className="font-barlow font-bold text-lg text-brand-navy mb-2">{step.title}</h3>
                        <p className="text-slate-600 font-dm text-sm">{step.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </motion.article>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 lg:py-24 bg-slate-50" id="faq">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-12">
              <span className="inline-block bg-brand-primary/10 text-brand-primary font-dm font-semibold px-4 py-2 rounded-full mb-4">
                Common Questions
              </span>
              <h2 className="font-barlow font-bold text-3xl sm:text-4xl text-brand-navy uppercase tracking-tight mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-slate-600 font-dm text-lg">
                Get answers to common questions about our {service.h1.replace(" Services in Dallas, TX", "").toLowerCase()} services.
              </p>
            </motion.div>

            <FAQAccordion faqs={service.faqs} />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-brand-navy">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div {...fadeInUp}>
              <h2 className="font-barlow font-bold text-3xl sm:text-4xl text-white uppercase tracking-tight mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-slate-300 font-dm text-lg mb-8 max-w-2xl mx-auto">
                Get your free, no-obligation quote today. We offer same-day service throughout Dallas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-brand-accent hover:bg-orange-600 text-white font-dm font-bold text-lg px-10 py-6 rounded-md"
                  data-testid="cta-quote"
                >
                  <Link to="/#contact" className="flex items-center gap-2">
                    Get Your Free Quote
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-brand-navy font-dm font-bold text-lg px-10 py-6 rounded-md"
                  data-testid="cta-phone"
                >
                  <a href={`tel:${PHONE_NUMBER}`} className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    {PHONE_NUMBER}
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-12">
              <h2 className="font-barlow font-bold text-2xl sm:text-3xl text-brand-navy uppercase tracking-tight mb-4">
                Other Services We Offer
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {Object.values(SERVICES_DATA)
                .filter(s => s.slug !== service.slug)
                .slice(0, 3)
                .map((relatedService, index) => {
                  const RelatedIcon = iconMap[relatedService.icon] || Home;
                  return (
                    <motion.div
                      key={relatedService.slug}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={`/services/${relatedService.slug}`}
                        className="block bg-slate-50 hover:bg-slate-100 rounded-xl p-6 border border-slate-200 transition-all hover:-translate-y-1"
                        data-testid={`related-service-${index}`}
                      >
                        <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center mb-4">
                          <RelatedIcon className="w-6 h-6 text-brand-primary" />
                        </div>
                        <h3 className="font-barlow font-bold text-lg text-brand-navy mb-2">
                          {relatedService.h1.replace(" Services in Dallas, TX", "")}
                        </h3>
                        <p className="text-slate-600 font-dm text-sm mb-4">{relatedService.shortDescription}</p>
                        <span className="text-brand-primary font-dm font-semibold text-sm flex items-center gap-1">
                          Learn More <ChevronRight className="w-4 h-4" />
                        </span>
                      </Link>
                    </motion.div>
                  );
                })}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <img src={LOGO_URL} alt="Dallas Haul Stars Logo" className="h-16 w-auto rounded-lg mb-4" />
                <p className="text-slate-400 font-dm text-sm">
                  Dallas Haul Stars LLC - Your trusted junk removal partner serving the Dallas-Fort Worth metroplex.
                </p>
              </div>
              <div>
                <h4 className="font-barlow font-bold text-lg mb-4">Quick Links</h4>
                <ul className="space-y-2 font-dm text-sm">
                  <li><Link to="/" className="text-slate-400 hover:text-white">Home</Link></li>
                  <li><Link to="/#services" className="text-slate-400 hover:text-white">Services</Link></li>
                  <li><Link to="/#reviews" className="text-slate-400 hover:text-white">Reviews</Link></li>
                  <li><Link to="/#contact" className="text-slate-400 hover:text-white">Get a Quote</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-barlow font-bold text-lg mb-4">Contact</h4>
                <ul className="space-y-2 font-dm text-sm">
                  <li className="flex items-center gap-2 text-slate-400">
                    <Phone className="w-4 h-4 text-brand-primary" />
                    <a href={`tel:${PHONE_NUMBER}`} className="hover:text-white">{PHONE_NUMBER}</a>
                  </li>
                  <li className="flex items-center gap-2 text-slate-400">
                    <MapPin className="w-4 h-4 text-brand-primary" />
                    Dallas, TX & Surrounding Areas
                  </li>
                  <li className="flex items-center gap-2 text-slate-400">
                    <Clock className="w-4 h-4 text-brand-primary" />
                    Mon-Sat: 7AM-7PM
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-slate-800 text-center">
              <p className="text-slate-500 text-sm font-dm">
                &copy; {new Date().getFullYear()} Dallas Haul Stars LLC. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ServicePage;
